import { useCallback, useRef, useState } from 'react'
import type { IExpertiseCategory } from './ExpertiseArticle'

type Props = {
    categories: IExpertiseCategory[]
    activeCategoryId: number | null
    onShowAll: () => void
    onSelectCategory: (category: IExpertiseCategory) => void
}

export const ExpertiseToolbar = ({
    categories,
    activeCategoryId,
    onShowAll,
    onSelectCategory,
}: Props) => {
    const count = 1 + categories.length
    const [focusIndex, setFocusIndex] = useState(0)
    const optionRefs = useRef<(HTMLLIElement | null)[]>([])

    const activateIndex = useCallback(
        (index: number) => {
            if (index === 0) {
                onShowAll()
            } else {
                onSelectCategory(categories[index - 1])
            }
        },
        [categories, onSelectCategory, onShowAll]
    )

    const moveToIndex = useCallback(
        (index: number) => {
            setFocusIndex(index)
            activateIndex(index)
            optionRefs.current[index]?.focus()
        },
        [activateIndex]
    )

    const onListboxKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
        switch (e.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                e.preventDefault()
                moveToIndex((focusIndex + 1) % count)
                break
            case 'ArrowLeft':
            case 'ArrowUp':
                e.preventDefault()
                moveToIndex((focusIndex - 1 + count) % count)
                break
            case 'Home':
                e.preventDefault()
                moveToIndex(0)
                break
            case 'End':
                e.preventDefault()
                moveToIndex(count - 1)
                break
            case 'Enter':
            case ' ':
                e.preventDefault()
                activateIndex(focusIndex)
                break
            default:
                break
        }
    }

    const optionClassName = (selected: boolean) =>
        `rounded-full py-2 px-4 text-sm mb-4 cursor-pointer outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0a8335] focus-visible:outline-offset-2 ${
            selected ? 'realisation-active' : 'realisation-inactive'
        }`

    return (
        <div className="mb-5">
            <h3
                id="expertise-filters-heading"
                className="text-lg font-semibold text-left mb-3 md:mb-4"
            >
                Filtrer
            </h3>
            <ul
                role="listbox"
                aria-labelledby="expertise-filters-heading"
                aria-controls="expertise-articles-grid"
                aria-orientation="horizontal"
                className="flex flex-wrap gap-2 md:gap-1 md:flex-row justify-start list-none p-0 m-0"
                onKeyDown={onListboxKeyDown}
            >
                <li
                    ref={(el) => {
                        optionRefs.current[0] = el
                    }}
                    id="expertise-filter-option-0"
                    role="option"
                    aria-selected={activeCategoryId === null}
                    tabIndex={focusIndex === 0 ? 0 : -1}
                    onClick={() => moveToIndex(0)}
                    className={optionClassName(activeCategoryId === null)}
                >
                    Toutes les compétences
                </li>
                {categories.map((category, index) => {
                    const i = index + 1
                    const selected = activeCategoryId === category.id
                    return (
                        <li
                            ref={(el) => {
                                optionRefs.current[i] = el
                            }}
                            key={category.id}
                            id={`expertise-filter-option-${i}`}
                            role="option"
                            aria-selected={selected}
                            tabIndex={focusIndex === i ? 0 : -1}
                            onClick={() => moveToIndex(i)}
                            className={optionClassName(selected)}
                        >
                            {category.attributes.name}
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
